'use client';

import type { ApplicationLogMetadata } from '@/lib/types';
import { mapToSentryLevel, type LogLevel, type LogCategory } from './types';
import { VALIDATION_ERROR_PATTERNS } from './constants';

import { cleanStackTrace, scrubMetadata } from './sentry-utils';

export interface ClientLogEntry {
  timestamp: Date;
  level: LogLevel;
  category: LogCategory;
  message: string;
  userId?: string;
  sessionId?: string;
  requestId?: string;
  metadata?: ApplicationLogMetadata;
  stackTrace?: string;
}

class ClientLogger {
  private logs: ClientLogEntry[] = [];
  private maxMemoryLogs = 100; // Keep fewer logs in memory on client
  private lastSentryTime: number = 0; // Rate limiting for Sentry

  /**
   * Log a message with specified level and category
   */
  async log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    metadata?: ApplicationLogMetadata,
    userId?: string,
    sessionId?: string,
    requestId?: string
  ): Promise<void> {
    // Scrub once before memory, database/server, console or Sentry can see it.
    metadata = scrubMetadata(metadata);

    const logEntry: ClientLogEntry = {
      timestamp: new Date(),
      level,
      category,
      message,
      userId,
      sessionId,
      requestId,
      metadata,
    };

    // Add to memory logs
    this.logs.unshift(logEntry);
    if (this.logs.length > this.maxMemoryLogs) {
      this.logs = this.logs.slice(0, this.maxMemoryLogs);
    }

    // Add stack trace for errors
    if (level === 'error' || level === 'fatal') {
      logEntry.stackTrace = new Error().stack;
    }

    // Console output for development
    if (process.env.NODE_ENV === 'development') {
      this.logToConsole(logEntry);
    }

    // Send to Sentry for errors and fatals (immediate)
    if (level === 'error' || level === 'fatal') {
      this.sendToSentry(logEntry);
    }

    // Send to server for database storage (background, non-blocking)
    if (level === 'error' || level === 'fatal') {
      // Don't await - let it run in background
      this.sendToServer(logEntry).catch(() => {
        // Silently fail - we don't want logging errors to break the app
      });
    }
  }

  /**
   * Convenience methods for different log levels
   */
  async debug(category: LogCategory, message: string, metadata?: ApplicationLogMetadata, userId?: string): Promise<void> {
    await this.log('debug', category, message, metadata, userId);
  }

  async info(category: LogCategory, message: string, metadata?: ApplicationLogMetadata, userId?: string): Promise<void> {
    await this.log('info', category, message, metadata, userId);
  }

  async warn(category: LogCategory, message: string, metadata?: ApplicationLogMetadata, userId?: string): Promise<void> {
    await this.log('warn', category, message, metadata, userId);
  }

  async error(category: LogCategory, message: string, metadata?: ApplicationLogMetadata, userId?: string, error?: Error): Promise<void> {
    const errorMetadata = withErrorFields(metadata, error);
    await this.log('error', category, message, errorMetadata, userId);
  }

  async fatal(category: LogCategory, message: string, metadata?: ApplicationLogMetadata, userId?: string, error?: Error): Promise<void> {
    const errorMetadata = withErrorFields(metadata, error);
    await this.log('fatal', category, message, errorMetadata, userId);
  }

  /**
   * Get logs from memory
   */
  getLogs(): ClientLogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear logs from memory
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Log to console for development
   */
  private logToConsole(logEntry: ClientLogEntry): void {
    const timestamp = logEntry.timestamp.toISOString();
    const prefix = `[${timestamp}] [${logEntry.level.toUpperCase()}] [${logEntry.category}]`;
    
    switch (logEntry.level) {
      case 'debug':
        console.debug(prefix, logEntry.message, logEntry.metadata);
        break;
      case 'info':
        console.info(prefix, logEntry.message, logEntry.metadata);
        break;
      case 'warn':
        console.warn(prefix, logEntry.message, logEntry.metadata);
        break;
      case 'error':
      case 'fatal':
        console.error(prefix, logEntry.message, logEntry.metadata);
        if (logEntry.stackTrace) {
          console.error('Stack trace:', cleanStackTrace(logEntry.stackTrace));
        }
        break;
    }
  }

  /**
   * Send error logs to Sentry (client-side)
   */
  private sendToSentry(logEntry: ClientLogEntry): void {
    try {
      // Only send critical errors to Sentry to conserve quota
      const shouldSendToSentry = 
        process.env.NODE_ENV === 'production' && 
        (logEntry.level === 'fatal' || 
         (logEntry.level === 'error' && this.isCriticalError(logEntry)));

      if (shouldSendToSentry) {
        // Import Sentry dynamically to avoid issues if not available
        import('@sentry/nextjs').then((Sentry) => {
          Sentry.captureException(new Error(logEntry.message), {
            tags: {
              category: logEntry.category,
              level: logEntry.level,
              source: 'client'
            },
            extra: {
              metadata: logEntry.metadata,
              userId: logEntry.userId,
              sessionId: logEntry.sessionId,
              requestId: logEntry.requestId
            },
            level: mapToSentryLevel(logEntry.level)
          });
        }).catch(() => {
          // Fallback to console if Sentry is not available
          console.error('SENTRY (CLIENT):', {
            level: logEntry.level,
            category: logEntry.category,
            message: logEntry.message,
            metadata: logEntry.metadata,
            stackTrace: logEntry.stackTrace
          });
        });
      } else if (process.env.NODE_ENV === 'development') {
        // In development, log all errors to console for debugging
        console.error('SENTRY (CLIENT):', {
          level: logEntry.level,
          category: logEntry.category,
          message: logEntry.message,
          metadata: logEntry.metadata,
          stackTrace: logEntry.stackTrace
        });
      }
    } catch (error) {
      console.error('Failed to send to Sentry (client):', error);
    }
  }

  /**
   * Determine if an error is critical enough for Sentry
   */
  private isCriticalError(logEntry: ClientLogEntry): boolean {
    const messageLower = logEntry.message.toLowerCase();
    const metadataMessage = logEntry.metadata?.errorMessage?.toLowerCase() || '';
    
    // Check if this is a validation error
    const isValidationError = VALIDATION_ERROR_PATTERNS.some(pattern => 
      messageLower.includes(pattern) || metadataMessage.includes(pattern)
    );

    if (isValidationError) {
      return false; // Don't send validation errors to Sentry
    }

    const criticalCategories = ['auth', 'security', 'payment'];
    const criticalMessages = [
      'authentication failed',
      'unauthorized',
      'payment failed',
      'security violation',
      'database connection',
      'server error'
    ];

    // Additional quota protection: rate limiting
    const now = Date.now();
    const lastSentryTime = this.lastSentryTime || 0;
    const timeSinceLastSentry = now - lastSentryTime;
    
    // Don't send more than 1 error per minute to Sentry
    if (timeSinceLastSentry < 60000) {
      return false;
    }

    const isCritical = criticalCategories.includes(logEntry.category) ||
           criticalMessages.some(msg => 
             logEntry.message.toLowerCase().includes(msg)
           );

    if (isCritical) {
      this.lastSentryTime = now;
    }

    return isCritical;
  }

  /**
   * Send logs to server for database storage
   */
  private async sendToServer(logEntry: ClientLogEntry): Promise<void> {
    try {
      // Call a server action to store the log
      // Use dynamic import with string literal to avoid build-time issues
      const loggingModule = await import('@/app/actions/logging');
      if (loggingModule && loggingModule.logClientError) {
        await loggingModule.logClientError({
          level: logEntry.level,
          category: logEntry.category,
          message: logEntry.message,
          metadata: logEntry.metadata,
          userId: logEntry.userId,
          sessionId: logEntry.sessionId,
          requestId: logEntry.requestId,
          stackTrace: logEntry.stackTrace
        });
      }
    } catch (error) {
      // Silently fail - we don't want logging errors to break the app
      console.debug('Failed to send log to server:', error);
    }
  }
}

// Export singleton instance
export const clientLogger = new ClientLogger();

// Copying metadata reads every property; a throwing getter must not turn a log
// call into a failure, so the copy falls back to the error fields alone.
function withErrorFields(metadata: ApplicationLogMetadata | undefined, error: Error | undefined): ApplicationLogMetadata {
  const readErrorFields = (): ApplicationLogMetadata => error
    ? { errorName: error.name, errorMessage: error.message, ...(error.stack ? { stackTrace: error.stack } : {}) }
    : {};
  try {
    return Object.assign({}, metadata || {}, readErrorFields());
  } catch {
    try {
      return Object.assign({ metadataScrubFailed: true }, readErrorFields());
    } catch {
      return { metadataScrubFailed: true };
    }
  }
}
